import { MouseEvent, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, type Theme } from '@mui/material/styles';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';
import Publish from '@mui/icons-material/Publish';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import { useQuery } from '@tanstack/react-query';
import { driveLabelsWriteAccessQuery } from '../../../api/drive-labels-api';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import {
  getLabelMenuActions,
  LabelMenuAction,
  LabelMenuActionKey,
} from './label-menu-actions';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

const ACTION_ICONS: Record<LabelMenuActionKey, typeof Delete> = {
  edit: Edit,
  publish: Publish,
  clone: ContentCopy,
  enable: CheckCircle,
  disable: Cancel,
  delete: Delete,
};

interface LabelActionsMenuProps {
  label: Label;
  /** Whether a rule currently locks the label from changes. */
  hasActiveLock: boolean;
  onPublish: (label: Label) => void;
  onClone: (label: Label) => void;
  onDelete: (label: Label) => void;
  onDisable: (label: Label) => void;
  onEnable: (label: Label) => void;
  /** A delete is running; a second one would hit labels that are already gone. */
  isDeleting?: boolean;
  isPublishing?: boolean;
  /** Opens Edit Label; the item only shows when this is given. */
  onEdit?: (label: Label) => void;
}

/** The Figma "Action Dropdown" colors, from the project's neutral scale (light) or theme (dark). */
function getMenuColors(theme: Theme) {
  if (theme.palette.mode === 'dark') {
    return {
      border: theme.palette.divider,
      hoverBackground: theme.palette.semantic.button.secondary.hover,
      label: theme.palette.text.primary,
      labelHover: theme.palette.text.primary,
      labelDisabled: theme.palette.text.disabled,
      hint: theme.palette.text.secondary,
      hintDisabled: theme.palette.text.disabled,
    };
  }
  const { neutral } = theme.palette.colors;
  return {
    border: neutral[400],
    hoverBackground: theme.palette.semantic.button.secondary.hover,
    label: neutral[700],
    labelHover: neutral[800],
    labelDisabled: neutral[400],
    hint: neutral[600],
    hintDisabled: neutral[400],
  };
}

export default function LabelActionsMenu({
  label,
  hasActiveLock,
  onPublish,
  onClone,
  onDelete,
  onDisable,
  onEnable,
  isDeleting = false,
  isPublishing = false,
  onEdit,
}: LabelActionsMenuProps) {
  const theme = useTheme();
  const colors = getMenuColors(theme);
  const { driveRoles } = useUserSettings();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const canPublish = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CreateDriveLabels.value
  );
  const canClone = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CreateDriveLabels.value
  );
  const canDelete = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.DeleteDriveLabels.value
  );
  // Disable and Enable are one permission, like Enable/Disable Drive Sweep.
  const canDisable = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.UpdateDriveLabelsStatus.value
  );
  const canEnable = canDisable;
  const canEdit =
    !!onEdit &&
    isAbleToSeeUIElement(driveRoles, DriveRoles.EditDriveLabels.value);
  const { data: hasWriteAccess, isLoading } = useQuery({
    ...driveLabelsWriteAccessQuery(),
    enabled:
      canPublish || canClone || canDelete || canDisable || canEnable || canEdit,
  });

  const actions = getLabelMenuActions(label, {
    hasActiveLock,
    canPublish,
    isPublishing,
    canClone,
    canEnable,
    canDisable,
    canDelete,
    canEdit: onEdit ? canEdit : undefined,
    isDeleting,
    hasWriteAccess: isLoading ? undefined : !!hasWriteAccess,
  });
  if (actions.length === 0) {
    return null;
  }

  const handlers: Record<LabelMenuActionKey, (label: Label) => void> = {
    edit: (label) => onEdit?.(label),
    publish: onPublish,
    clone: onClone,
    enable: onEnable,
    disable: onDisable,
    delete: onDelete,
  };
  const title = label.properties?.title ?? 'label';

  // The menu renders in a portal, but React events still bubble to the row, which opens the
  // details drawer, so both the button and the menu stop them.
  const stopRowClick = (event: MouseEvent) => event.stopPropagation();

  const renderItem = (action: LabelMenuAction) => {
    const ActionIcon = ACTION_ICONS[action.key];
    const item = (
      <MenuItem
        key={action.key}
        disabled={action.disabled}
        onClick={() => {
          setAnchorEl(null);
          handlers[action.key](label);
        }}
        sx={{
          alignItems: 'flex-start',
          gap: '8px',
          minHeight: 0,
          py: '4px',
          pl: '4px',
          pr: '8px',
          borderRadius: '2px',
          whiteSpace: 'normal',
          color: colors.label,
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: colors.hoverBackground,
            color: colors.labelHover,
          },
          // Disabled items are greyed by their own colors, not MUI's default opacity.
          '&.Mui-disabled': { opacity: 1, color: colors.labelDisabled },
        }}
      >
        <ActionIcon sx={{ flexShrink: 0, color: 'inherit' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            component="span"
            sx={{
              py: '2px',
              fontSize: 14,
              lineHeight: 1.43,
              letterSpacing: '0.17px',
              color: 'inherit',
            }}
          >
            {action.label}
          </Typography>
          {action.hint && (
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                lineHeight: '20px',
                letterSpacing: '0.4px',
                color: action.disabled ? colors.hintDisabled : colors.hint,
              }}
            >
              {action.hint}
            </Typography>
          )}
        </Box>
      </MenuItem>
    );

    // A greyed-out item ignores the pointer, so the tooltip sits on a wrapper, as in the other grids.
    return action.tooltip ? (
      <Tooltip key={action.key} arrow placement="left" title={action.tooltip}>
        <span>{item}</span>
      </Tooltip>
    ) : (
      item
    );
  };

  return (
    <>
      <Tooltip arrow title="Actions">
        <IconButton
          size="small"
          aria-label={`Actions for ${title}`}
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={stopRowClick}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              border: `0.5px solid ${colors.border}`,
              borderRadius: '4px',
              boxShadow: '4px 6px 20px 6px rgba(0, 0, 0, 0.08)',
            },
          },
          list: {
            sx: { display: 'flex', flexDirection: 'column', gap: '4px' },
          },
        }}
      >
        {actions.map(renderItem)}
      </Menu>
    </>
  );
}
